import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MonitorPlay } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/apiClient";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030305] px-6">
      <motion.div
        {...fadeUp}
        className="w-full max-w-md bg-[#0A0A0F] border border-white/10 rounded-sm p-8 md:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <MonitorPlay className="w-6 h-6 text-[#FF2A2A]" strokeWidth={2.5} />
          <span className="font-display text-xl tracking-tighter uppercase">QTV<span className="text-[#FF2A2A]">Network</span></span>
        </Link>
        <h1 className="font-display text-3xl uppercase tracking-tight mb-1">Set a new password</h1>
        <p className="text-sm text-zinc-500 mb-8">Choose a new password for your account.</p>

        {error && (
          <div data-testid="reset-error" className="mb-4 text-sm text-[#FF4D4D] bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 px-4 py-2.5 rounded-sm">
            {error}
          </div>
        )}

        {!token ? (
          <div data-testid="reset-no-token" className="text-sm text-zinc-400">
            This reset link is missing its token. Please{" "}
            <Link to="/forgot-password" className="text-white hover:text-[#FF2A2A]">request a new link</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-zinc-500">New password (min 6 characters)</label>
              <input
                data-testid="reset-password-input"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-zinc-500">Confirm new password</label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
              />
            </div>
            <button
              data-testid="reset-submit-button"
              disabled={busy}
              className="w-full bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white py-3 rounded-sm font-semibold transition-colors"
            >
              {busy ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-zinc-500 text-center">
          <Link to="/login" data-testid="go-login-link" className="text-white hover:text-[#FF2A2A] transition-colors">
            Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
